import { useEffect, useState } from "react";
import { ClipboardCheck, Clock3, X } from "lucide-react";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
export const abs = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);
export const statusLabel = {
  pending_review: "Needs review",
  live: "Live auction",
  closed: "Auction closed",
  awarded: "Awarded",
  dispatched: "In transit",
  completed: "Completed",
  matching: "Matching agencies",
  quoted: "Agency quotes",
  proofing: "Proofing",
  in_progress: "In design",
  delivered: "Proof delivered",
  revision: "Revisions requested",
  approved: "Ready for print",
};

// ============================================================
// COUNTDOWN — live ticker for sealed auctions
// ============================================================
export function useCountdown(endsAt) {
  const [remaining, setRemaining] = useState(() => diffMs(endsAt));
  useEffect(() => {
    setRemaining(diffMs(endsAt));
    if (!endsAt) return;
    const t = setInterval(() => setRemaining(diffMs(endsAt)), 1000);
    return () => clearInterval(t);
  }, [endsAt]);
  return remaining;
}

export function diffMs(endsAt) {
  if (!endsAt) return null;
  return new Date(endsAt).getTime() - Date.now();
}

export function fmtCountdown(ms) {
  if (ms === null || ms === undefined) return "—";
  if (ms <= 0) return "Closed";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h >= 1) return `${h}h ${String(m).padStart(2, "0")}m ${String(sec).padStart(2, "0")}s`;
  if (m >= 1) return `${m}m ${String(sec).padStart(2, "0")}s`;
  return `${sec}s`;
}

export function Countdown({ endsAt, size = "sm", onExpire }) {
  const ms = useCountdown(endsAt);
  const expired = ms !== null && ms <= 0;
  useEffect(() => {
    if (expired && onExpire) onExpire();
  }, [expired, onExpire]);
  const urgent = ms !== null && ms > 0 && ms < 30 * 60 * 1000; // < 30 min
  const cls = `countdown ${size} ${expired ? "expired" : urgent ? "urgent" : "normal"}`;
  return (
    <span className={cls} data-testid={`countdown-${size}`}>
      <Clock3 size={size === "lg" ? 14 : 11}/>
      {fmtCountdown(ms)}
    </span>
  );
}

// Map an authenticated user's role → supplier_id (owner's supplier profile if applicable)
export const roleToSupplierId = (u) => {
  if (!u) return null;
  if (["printer", "agency", "material_supplier", "finishing_partner"].includes(u.role)) return u.id;
  return null;
};

export function formatRelative(iso) {
  if (!iso) return "";
  try {
    const t = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - t);
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d ago`;
    return new Date(iso).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" });
  } catch { return ""; }
}

export const Stat = ({ icon: Icon, label, value, note, accent }) => (
  <div className={accent ? "stat accent" : "stat"}>
    <div className="stat-icon"><Icon size={17}/></div>
    <span>{label}</span>
    <strong data-testid={`metric-${label.toLowerCase().replaceAll(" ", "-")}`}>{value}</strong>
    <small>{note}</small>
  </div>
);

export const AnalyticsTile = ({ label, value, note, tone, testid }) => (
  <div className={`analytics-tile tone-${tone}`} data-testid={testid}>
    <span className="atile-label">{label}</span>
    <strong>{value}</strong>
    <small>{note}</small>
  </div>
);

export function btnStyle(bg, color) {
  return { background: bg, color, border: "none", padding: "7px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer" };
}



export const th = {padding:"8px 10px",fontSize:11,fontWeight:600,color:"#374151",borderBottom:"1px solid #e5e7eb"};
export const td = {padding:"10px",verticalAlign:"top"};
export const inp = {width:"100%",padding:"8px 10px",border:"1px solid #d1d5db",borderRadius:8,marginTop:4,fontSize:13};

export function StatPill({ label, value, testid, tone }) {
  const bg = tone === "ok" ? "#DCFCE7" : tone === "warn" ? "#FEF3C7" : "#F3F4F6";
  const fg = tone === "ok" ? "#065F46" : tone === "warn" ? "#92400E" : "#111827";
  return (
    <div data-testid={testid} style={{background:bg,color:fg,padding:"8px 14px",borderRadius:8,minWidth:120}}>
      <div style={{fontSize:10,textTransform:"uppercase",letterSpacing:0.5,opacity:0.7}}>{label}</div>
      <div style={{fontSize:18,fontWeight:700,marginTop:2}}>{value}</div>
    </div>
  );
}

export const Modal = ({ title, close, children }) => (
  <div className="modal-backdrop">
    <div className="modal">
      <div className="modal-head">
        <div><p className="eyebrow">GO PRINT PACK</p><h2>{title}</h2></div>
        <button data-testid="modal-close" className="close-btn" onClick={close}><X size={18}/></button>
      </div>
      {children}
    </div>
  </div>
);

export const Empty = ({ text }) => (
  <div className="empty"><ClipboardCheck size={24}/><span>{text}</span></div>
);

export const QUANTITY_UNITS = [
  ["units", "Units (or Pieces)"], ["kgs", "Kilograms (Kgs)"], ["mt", "Metric Tonnes (MT)"], ["sq_m", "Square Meters (sq m)"],
  ["m", "Linear Meters (m)"], ["rolls", "Rolls"], ["sheets", "Sheets (or Impressions)"], ["reams", "Reams"], ["sets", "Sets"],
];
const UNIT_SHORT = { units: "units", kgs: "Kgs", mt: "MT", sq_m: "sq m", m: "m", rolls: "rolls", sheets: "sheets", reams: "reams", sets: "sets" };
export const qtyLabel = (job) => `${Number(job?.quantity || 0).toLocaleString("en-IN")} ${UNIT_SHORT[job?.quantity_unit] || "units"}`;
const UNIT_ONE = { units: "unit", kgs: "Kg", mt: "MT", sq_m: "sq m", m: "m", rolls: "roll", sheets: "sheet", reams: "ream", sets: "set" };
export const perUnit = (price, qty, unit) => {
  const p = Number(price), q = Number(qty);
  if (!(p > 0) || !(q > 0)) return "";
  const v = p / q;
  return `₹${v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: v < 1 ? 4 : 2 })} / ${UNIT_ONE[unit] || unit || "unit"}`;
};
export const PerUnit = ({ price, qty, unit, testid, style, suffix = "excl. GST" }) => {
  const t = perUnit(price, qty, unit);
  return t ? <small data-testid={testid} style={{ display: "block", fontSize: 11, fontWeight: 500, color: "#64748b", ...style }}>{t} <span style={{ opacity: 0.75 }}>{suffix}</span></small> : null;
};
